import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  CardActions,
  CardContent,
  Grid,
  MenuItem,
  TextField,
} from "@material-ui/core";
import { ThemeWrapper } from "../common/ThemeWrapper";
import { HoseSeries } from "../../constants/HoseSeries";
import { adjustMapping } from "../../utils/calculationsT";
import { getOr, flowRight } from "lodash/fp";
import { findByLabel, findByValue } from "../../utils/lookups";
import { useLabelStyles, useStyles } from "../common/styles";
import { ContentContainer } from "../common/ContentContainer";
import "../../Style.css";

export const TemperatureCalculator = ({
  ConfigurationTooCalculator,
  resultCalculation,
  setResultPressureCalculation,
}) => {
  const classes = useStyles();
  //Values for getting Model attributes
  const [selectedProduct, setSelectedProduct] = useState("");
  const [selectedModel, setSelectedModel] = useState("");
  const [pressure, setPressure] = useState("");
  const [selectedLabel, setSelectedLabel] = useState("");

  //Input Value
  const [applicationTemp, setApplicationTemp] = useState();
  //Result Values
  const [singleMAWP, setSingleMAWP] = useState("");
  const [doubleMAWP, setDoubleMAWP] = useState("");
  const [tripleMAWP, setTripleMAWP] = useState("");
  const [quadMAWP, setQuadMAWP] = useState("");
  const [barPressure, setBarPressure] = useState("");

  //Error Values
  const [temperatureError, setTemperatureError] = useState(false);
  const [tempErrorText, setTempErrorText] = useState("");

  const productChoices = useMemo(
    () =>
      flowRight(
        getOr([], "products"),
        findByValue(selectedProduct)
      )(HoseSeries),
    [selectedProduct]
  );

  const modelInfo = useMemo(
    () =>
      !ConfigurationTooCalculator || ConfigurationTooCalculator?.braids === 1
        ? findByValue(selectedModel)(productChoices)
        : findByLabel(selectedLabel)(productChoices),
    [ConfigurationTooCalculator, selectedLabel, selectedModel, productChoices]
  );

  const modelAlloy = flowRight(
    getOr("", "alloy"),
    findByValue(selectedProduct)
  )(HoseSeries);

  const handleApplicationTempChange = useCallback(
    (e) => {
      setApplicationTemp(e.target.value);
    },
    [setApplicationTemp]
  );

  const validate = useCallback(() => {
    var max304Temp = 1500;
    var maxMonelTemp = 800;
    var maxBronzeTemp = 450;
    var max625Temp = 1800;
    var max776Temp = 800;
    var max1400Temp = 1500;
    var maxP5Temp = 1500;

    if (applicationTemp > max304Temp && modelAlloy === "304") {
      setTemperatureError(true);
      setTempErrorText("Max temperature for 304/304L alloy is 1500°F");
      return true;
    } else if (applicationTemp > maxMonelTemp && modelAlloy === "Monel") {
      setTemperatureError(true);
      setTempErrorText("Max temperature for Monel alloy is 800°F");
      return true;
    } else if (applicationTemp > maxBronzeTemp && modelAlloy === "Bronze") {
      setTemperatureError(true);
      setTempErrorText("Max temperature for Bronze alloy is 450°F");
      return true;
    } else if (applicationTemp > max625Temp && modelAlloy === "Inconel 625") {
      setTemperatureError(true);
      setTempErrorText("Max temperature for Inconel 625 alloy is 1800°F");
      return true;
    } else if (
      applicationTemp > max776Temp &&
      modelAlloy === "Hastelloy C276"
    ) {
      setTemperatureError(true);
      setTempErrorText("Max temperature for Hastelloy C276 alloy is 800°F");
      return true;
    } else if (applicationTemp > max1400Temp && modelAlloy === "304") {
      setTemperatureError(true);
      setTempErrorText("Max temperature for 304 alloy is 1500°F");
      return true;
    } else if (applicationTemp > maxP5Temp && modelAlloy === "304") {
      setTemperatureError(true);
      setTempErrorText("Max temperature for 304 alloy is 1500°F");
      return true;
    } else {
      setTemperatureError(false);
      setTempErrorText();
      return false;
    }
  }, [applicationTemp, modelAlloy]);

  const calculate = useCallback(() => {
    if (!applicationTemp) {
      return;
    }
    validate();
    //Error Check
    if (adjustMapping[modelAlloy](parseInt(applicationTemp)) === -1) {
      setSingleMAWP();
      setDoubleMAWP();
      setTripleMAWP();
      setQuadMAWP();
      return;
    }
    setSingleMAWP(
      (
        adjustMapping[modelAlloy](parseInt(applicationTemp)) *
        modelInfo?.singleBraid
      ).toFixed(2)
    );
    setDoubleMAWP(
      (
        adjustMapping[modelAlloy](parseInt(applicationTemp)) *
        modelInfo?.doubleBraid
      ).toFixed(2)
    );
    setTripleMAWP(
      (
        adjustMapping[modelAlloy](parseInt(applicationTemp)) *
        modelInfo?.tripleBraid
      ).toFixed(2)
    );
    setQuadMAWP(
      (
        adjustMapping[modelAlloy](parseInt(applicationTemp)) *
        modelInfo?.quadBraid
      ).toFixed(2)
    );
  }, [modelAlloy, modelInfo, applicationTemp, validate]);

  const calculateBar = useCallback(() => {
    if (!applicationTemp) {
      return;
    }
    if (adjustMapping[modelAlloy](parseInt(applicationTemp)) === -1) {
      return;
    }
    setBarPressure(
      adjustMapping[modelAlloy](parseInt(applicationTemp)) * pressure
    );
  }, [modelAlloy, applicationTemp, pressure]);

  const handleHoseSelection = (e) => {
    e.persist();
    setSelectedProduct(e.target.value);
    if (e.target.value === "P5") {
      alert("No testing data available for double braided P5. 0 psi used.");
    }
    //Reset dependent values
    setApplicationTemp("");
    setTemperatureError("");
    setTempErrorText("");
    setSingleMAWP("");
    setDoubleMAWP("");
    setTripleMAWP("");
    setQuadMAWP("");
  };

  const handleModelSelection = (e) => {
    e.persist();
    setSelectedModel(e.target.value);
    //console.log(selectedModel);
    //Reset dependent values
    setTemperatureError("");
    setTempErrorText("");
    setSingleMAWP("");
    setDoubleMAWP("");
    setTripleMAWP("");
    setQuadMAWP("");
  };

  const reset = useCallback(() => {
    setSelectedProduct("");
    setSelectedModel("");
    setApplicationTemp("");
    setDoubleMAWP("");
    setSingleMAWP("");
    setTripleMAWP("");
    setQuadMAWP("");
    setTemperatureError("");
    setTempErrorText("");
  }, []);

  const labelClasses = useLabelStyles();
  const labelProps = {
    classes: {
      root: labelClasses.root,
    },
  };
  const inputProps = {
    classes: {
      root: classes.inputRoot,
      disabled: classes.disabled,
    },
  };

  useEffect(() => {
    if (
      ConfigurationTooCalculator?.series &&
      ConfigurationTooCalculator?.value &&
      ConfigurationTooCalculator?.temperature &&
      ConfigurationTooCalculator?.pressure &&
      ConfigurationTooCalculator?.label
    ) {
      setSelectedProduct(ConfigurationTooCalculator.series);
      setSelectedModel(ConfigurationTooCalculator.value);
      setApplicationTemp(ConfigurationTooCalculator.temperature);
      setPressure(ConfigurationTooCalculator.pressure);
      setSelectedLabel(ConfigurationTooCalculator.label);
    }
    calculate();
    calculateBar();
  }, [calculate, calculateBar, ConfigurationTooCalculator, modelInfo]);

  useEffect(() => {
    if (ConfigurationTooCalculator) {
      setResultPressureCalculation(barPressure);
      resultCalculation(
        singleMAWP !== "NaN" &&
          !!singleMAWP &&
          `${
            ConfigurationTooCalculator.braids === 1
              ? singleMAWP
              : ConfigurationTooCalculator.braids === 2
              ? doubleMAWP
              : ConfigurationTooCalculator.braids === 3
              ? tripleMAWP
              : quadMAWP
          }`
      );
    }
  }, [
    ConfigurationTooCalculator,
    barPressure,
    singleMAWP,
    doubleMAWP,
    tripleMAWP,
    quadMAWP,
    resultCalculation,
    setResultPressureCalculation,
  ]);

  if (ConfigurationTooCalculator) {
    return null;
  }

  return (
    <ThemeWrapper title={"Temperature Adjustment Calculator"}>
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              select
              id="product-selection"
              label={"Choose Hose Series"}
              value={selectedProduct}
              onChange={handleHoseSelection}
              fullWidth
            >
              {HoseSeries.map((option) => (
                <MenuItem key={option.label} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              select
              label={"Pick Part Number"}
              value={selectedModel}
              onChange={handleModelSelection}
              disabled={!productChoices || productChoices.length < 1}
              fullWidth
            >
              {productChoices.map((option) => (
                <MenuItem key={option.label} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} lg={3}>
            <TextField
              value={applicationTemp}
              error={temperatureError}
              helperText={tempErrorText}
              disabled={!selectedModel || selectedModel.length < 1}
              label={"Enter Application Temperature"}
              type="number"
              onChange={handleApplicationTempChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <ContentContainer>
              <Grid container spacing={2}>
                {/* <Grid item xs={12} lg={3}>
                      <TextField 
                        select
                        label={'Select a Specific Model'}
                        value={selectedModel}
                        onChange={handleModelSelection}
                        disabled={!productChoices || productChoices.length < 1}
                        fullWidth
                      >
                      {
                        productChoices.map((option) => (
                          <MenuItem key={option.label} value={option.value} >
                              {option.label}
                          </MenuItem>
                          ))
                      }
                      </TextField>
                    </Grid> */}

                {selectedProduct === "1400" || selectedModel === 96 ? (
                  <div></div>
                ) : (
                  <Grid item xs={12} lg={4}>
                    <TextField
                      label={"Working Pressure (1x-Braid) @ 70°F (PSIG)"}
                      value={getOr("", "singleBraid", modelInfo)}
                      disabled
                      fullWidth
                    />
                  </Grid>
                )}
                {selectedProduct === "1400" || selectedModel === 96 ? (
                  <div></div>
                ) : (
                  <Grid item xs={12} lg={4}>
                    <TextField
                      label={"Working Pressure (2x-Braid) @ 70°F (PSIG)"}
                      value={getOr("", "doubleBraid", modelInfo)}
                      disabled
                      fullWidth
                    />
                  </Grid>
                )}

                {(selectedProduct === "1400" || selectedModel === 58) &&
                selectedModel !== 96 ? (
                  <Grid item xs={9} lg={3}>
                    <TextField
                      label={"Working Pressure (3x-Braid) @ 70°F (PSIG)"}
                      value={getOr("", "tripleBraid", modelInfo)}
                      disabled
                      fullWidth
                    />
                  </Grid>
                ) : (
                  <div> </div>
                )}

                {selectedModel === 96 ? (
                  <Grid item xs={12} lg={3}>
                    <TextField
                      label={"Working Pressure (4x-Braid) @ 70°F (PSIG)"}
                      value={getOr("", "quadBraid", modelInfo)}
                      disabled
                      fullWidth
                    />
                  </Grid>
                ) : (
                  <div> </div>
                )}

                <Grid container spacing={3}>
                  {selectedProduct === "1400" || selectedModel === 96 ? (
                    <div></div>
                  ) : (
                    <Grid item xs={12} lg={6}>
                      <TextField
                        label={`Working Pressure (1x-Braid) @ Application Temperature`}
                        variant="filled"
                        InputLabelProps={labelProps}
                        disabled={true}
                        InputProps={inputProps}
                        value={singleMAWP}
                        fullWidth
                      />
                    </Grid>
                  )}
                  {selectedProduct === "1400" || selectedModel === 96 ? (
                    <div></div>
                  ) : (
                    <Grid item xs={12} lg={6}>
                      <TextField
                        label={`Working Pressure (2x-Braid) @ Application Temperature`}
                        variant="filled"
                        InputLabelProps={labelProps}
                        disabled={true}
                        InputProps={inputProps}
                        value={doubleMAWP}
                        fullWidth
                      />
                    </Grid>
                  )}
                  {(selectedProduct === "1400" || selectedModel === 58) &&
                  selectedModel !== 96 ? (
                    <Grid item xs={12} lg={6}>
                      <TextField
                        label={`Working Pressure (3x-Braid) @ Application Temperature`}
                        variant="filled"
                        InputLabelProps={labelProps}
                        disabled={true}
                        InputProps={inputProps}
                        value={tripleMAWP}
                        fullWidth
                      />
                    </Grid>
                  ) : (
                    <div></div>
                  )}

                  {selectedModel === 96 ? (
                    <Grid item xs={12} lg={6}>
                      <TextField
                        label={`Working Pressure (4x-Braid) @ Application Temperature`}
                        variant="filled"
                        InputLabelProps={labelProps}
                        disabled={true}
                        InputProps={inputProps}
                        value={quadMAWP}
                        fullWidth
                      />
                    </Grid>
                  ) : (
                    <div></div>
                  )}
                </Grid>
              </Grid>
            </ContentContainer>
          </Grid>
        </Grid>
      </CardContent>
      <CardActions style={{ justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          color="default"
          disabled={!applicationTemp}
          style={{ float: "left" }}
          onClick={reset}
        >
          {"Reset"}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!applicationTemp}
          style={{ float: "right" }}
          onClick={calculate}
        >
          {"Calculate"}
        </Button>
      </CardActions>
    </ThemeWrapper>
  );
};

export default TemperatureCalculator;
